import React, { useEffect, useState } from "react";
import { Button, Modal, Checkbox, Typography, message, Image, Input, Spin } from "antd";
import { getPreSignedUrls, uploadFileToS3 } from "../utils/ExtraFunctions";
import { EditOutlined } from "@ant-design/icons";
import { FaRegFilePdf } from "react-icons/fa";
import { Loading } from "./Loading";

const { Text } = Typography;

const MultipleUpload = ({ showViewFileModal, setShowViewFileModal, files: incomingFiles, loading, setLoading, fileInfo, setSelectedFiles, setFileInfo, setDuplicateUpload, duplicateUpload, uploadTemp, uploadType, handleUploadFunc, showInputFileName, userAuthKey }) => {
  const [localFiles, setLocalFiles] = useState([]);
  const [editingFileName, setEditingFileName] = useState(null);
  const [editedNameValue, setEditedNameValue] = useState("");
  const anyFileSelected = localFiles.some((file) => file.selected);

  useEffect(() => {
    if (showViewFileModal && incomingFiles?.length) {
      const initialized = incomingFiles.map((file) => ({
        ...file,
        selected: true,
        originalName: file.name,
      }));
      setLocalFiles(initialized);
    }
  }, [showViewFileModal, incomingFiles]);

  const handleCancelFileView = () => {
    setShowViewFileModal(false);
    setSelectedFiles([]);
  };

  const MAX_FILE_SIZE_MB = 100;
  const totalSizeMB = localFiles?.filter((file) => file.selected).reduce((total, file) => total + (file?.size || 0), 0) / (1024 * 1024);
  const isFileSizeExceeded = totalSizeMB > MAX_FILE_SIZE_MB;

  const handleOkFileView = async () => {
    try {
      setLoading(true);
      const selectedFiles = localFiles.filter((file) => file.selected);

      let updatedFileInfo = fileInfo?.filter(
        (fileData) => fileData?.file instanceof File && selectedFiles.some((f) => f.originalName === fileData.filename)
      );

      let filenames = updatedFileInfo?.map((fileData) => fileData.filename);

      if (filenames?.length === 0) {
        message.error("No valid files to upload.");
        setShowViewFileModal(false);
        setLoading(false);
        return;
      }

      const preSignedUrls = await getPreSignedUrls({filenames, userAuthKey});
      if (!preSignedUrls) {
        message.error("No valid files to upload.");
        setShowViewFileModal(false);
        setLoading(false);
        return;
      }

      const uploadedFilesInfo = [];

      for (const fileData of updatedFileInfo) {
        const { url: uploadUrl, fileName } = preSignedUrls?.find((item) => item?.fileName === fileData?.filename) || {};

        if (!uploadUrl) {
          message.error(`URL not found for file: ${fileData?.filename}`);
          continue;
        }

        const finalFileLink = await uploadFileToS3(fileData?.file, uploadUrl);
        if (finalFileLink) {
          let fileType;
          if (fileData?.file?.type === "application/pdf") {
            fileType = "PDF";
          } else if (fileData?.file?.type?.startsWith("image/")) {
            fileType = "IMAGE";
          }

          
          const matchingLocal = selectedFiles.find((f) => f.originalName === fileData.filename);
          uploadedFilesInfo.push({
            fileName: matchingLocal?.name || fileName,
            fileLink: finalFileLink,
            ...(fileType && { fileType }),
          });
        }
      }

      setLoading(false);
      setShowViewFileModal(false);
      uploadTemp(uploadedFilesInfo);
      setSelectedFiles([]);
      setLocalFiles([]);
      setFileInfo([]);
    } catch (error) {
      message.error("An unexpected error occurred during file upload.");
      setLoading(false);
    }
  };

  const handleEdit = (file) => {
    setEditingFileName(file.name);
    setEditedNameValue(file.name);
  };

  const handleSaveEditedName = (file) => {
    const updatedFiles = localFiles.map((f) => {
      if (f.originalName === file.originalName) {
        
        return { ...f, name: editedNameValue };
      }
      return f;
    });

    setLocalFiles(updatedFiles);
    setEditingFileName(null);
    setEditedNameValue("");
  };

  return (
    <Modal
      title={<div className="commonModalTitle">Upload Files</div>}
      visible={showViewFileModal}
      onOk={handleOkFileView}
      onCancel={handleCancelFileView}
      bodyStyle={{ maxHeight: "400px", overflowY: "auto" }}
      footer={[
        <div style={{ display: "flex", flexDirection: "column", width: "100%" }} key="footer">
          {isFileSizeExceeded && (
            <Text type="danger" style={{ marginBottom: "10px", textAlign: "center" }}>
              Total file size exceeded **100 MB**. Please remove some files.
            </Text>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
            <span>
              <strong>Total Size:</strong> {totalSizeMB.toFixed(2)} MB
            </span>
            <div>
              <Button onClick={handleCancelFileView}>Cancel</Button>
              <Button onClick={handleOkFileView} type="primary" disabled={isFileSizeExceeded || !anyFileSelected || loading}>
                OK
              </Button>
            </div>
          </div>
        </div>,
      ]}
    >
      {loading ? (
          <Spin size="large" style={{margin:"10% 0 10% 45%"}} />
      ) : (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "20px",
            maxHeight: "55vh",
            overflowY: "auto",
            padding: "10px",
          }}
        >
          {localFiles?.map((file, index) => (
            <div
              key={index}
              style={{
                width: "100%",
                maxWidth: "520px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderRadius: "8px",
                backgroundColor: "#fafafa",
                marginBottom: "5px",
              }}
            >
              {file?.type?.startsWith("image/") || uploadType === "IMAGE" ? <Image src={file?.url} width={40} height={40} style={{ objectFit: "cover" }} preview={true} /> : <FaRegFilePdf style={{ color: "red", marginRight: "5px", fontSize: "22px" }} />}

              <div style={{ flex: 1, marginLeft: "15px", overflow: "hidden" }}>
                {editingFileName === file?.name ? (
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "5px", width: "95%" }}>
                    <Input value={editedNameValue} onChange={(e) => setEditedNameValue(e.target.value)} style={{ flex: 1 }} />
                    <Button type="primary" onClick={() => handleSaveEditedName(file)} style={{ padding: "0 10px" }}>
                      Done
                    </Button>
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: "14px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {file?.name}
                    </div>
                    <div style={{ fontSize: "12px", color: "#888" }}>{(file?.size / (1024 * 1024)).toFixed(2)} MB</div>
                  </>
                )}
              </div>

              {editingFileName !== file.name && <EditOutlined style={{ marginRight: "20px", cursor: "pointer" }} onClick={() => handleEdit(file)} />}

              <Checkbox
                checked={file.selected}
                onChange={(e) => {
                  const updatedFiles = localFiles.map((f) => (f.name === file.name ? { ...f, selected: e.target.checked } : f));
                  setLocalFiles(updatedFiles);
                }}
              />
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

export default MultipleUpload;
